import React from 'react';
import {
  Button,
  Typography,
  Grid,
  Box,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { MetActivityDetail } from '../../../services/admin-met-activity.service';
import { splitKeywords } from '../../../services/met-activity.service';
import { BaseDialog } from '@components/common/BaseDialog';
import { Locale } from '../../../services/workout.service';

interface MetActivityDetailDialogProps {
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  data: MetActivityDetail | null;
  isLoading: boolean;
}

const LOCALES: Locale[] = ['uk', 'en', 'ru', 'cs', 'es', 'pl', 'be'];

const COMPENDIUM_LABELS: Record<string, string> = {
  adult: 'Adult (19–59 р.)',
  youth: 'Youth (2–18 р.)',
  elderly: 'Elderly (60+ р.)',
};

export const MetActivityDetailDialog: React.FC<MetActivityDetailDialogProps> = ({
  open,
  onClose,
  onEdit,
  data,
  isLoading,
}) => {
  if (!data && !isLoading) return null;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      title={
        <>
          Деталі MET активності #{data?.id}
          {data && (
            <Typography
              component="span"
              variant="subtitle1"
              sx={{ ml: 1, fontFamily: 'monospace', color: 'text.secondary' }}
            >
              [{data.code}]
            </Typography>
          )}
        </>
      }
      actions={
        <Button onClick={onEdit} startIcon={<EditIcon />} color="primary" variant="contained">
          Редагувати
        </Button>
      }
    >
        {isLoading ? (
          <Typography>Завантаження...</Typography>
        ) : data ? (
          <Grid container spacing={3}>
            {/* Left: Metadata */}
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={COMPENDIUM_LABELS[data.compendiumType] ?? data.compendiumType}
                  color="primary"
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
                {data.youthAgeGroupLabel && (
                  <>
                    <br />
                    <Chip
                      label={`Вік: ${data.youthAgeGroupLabel}`}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </>
                )}
              </Box>

              <Typography variant="subtitle2" color="text.secondary">Категорія</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                [{data.categoryCode}] {data.categoryName}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">MET значення</Typography>
              <Typography variant="h4" color="primary" fontWeight="bold" sx={{ mb: 2 }}>
                {data.metValue}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                1 MET = 1 kcal/kg/год
              </Typography>
            </Grid>

            {/* Right: Translations */}
            <Grid item xs={12} md={8}>
              <Typography variant="h6" gutterBottom>Оригінальна назва (EN)</Typography>
              <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic' }}>
                {data.nameEn}
              </Typography>

              <Typography variant="h6" gutterBottom>Переклади</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '60px' }}>Мова</TableCell>
                      <TableCell>Назва</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {LOCALES.map(lang => {
                      const translation = data.nameI18n?.[lang];
                      return (
                        <TableRow key={lang}>
                          <TableCell sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
                            {lang}
                          </TableCell>
                          <TableCell>
                            {translation
                              ? translation
                              : <Typography variant="caption" color="text.secondary">Немає перекладу</Typography>
                            }
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="h6" gutterBottom>Ключові фрази</Typography>
              {splitKeywords(data.keywords).length ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                  {splitKeywords(data.keywords).map(k => <Chip key={k} label={k} size="small" />)}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Немає</Typography>
              )}
            </Grid>

            {/* Footer: Timestamps */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon /> Дати
              </Typography>
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Створено</Typography>
                  <Typography variant="body2">{new Date(data.createdAt).toLocaleString()}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Оновлено</Typography>
                  <Typography variant="body2">{new Date(data.updatedAt).toLocaleString()}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Typography color="error">Не вдалося завантажити дані</Typography>
        )}
    </BaseDialog>
  );
};
